import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { MeetingCreateDialogComponent } from './meeting-create-dialog/meeting-create-dialog.component';
import { MeetingDetailDialogComponent } from './meeting-detail-dialog/meeting-detail-dialog.component';
import { MeetingsService } from './meetings.service';

@Component({
  selector: 'app-meetings-page',
  templateUrl: './meetings-page.component.html',
  styleUrls: ['./meetings-page.component.scss']
})
export class MeetingsPageComponent implements OnInit {
  loading = false;
  meetings: any[] = [];
  staffOptions: any[] = [];
  activeTab = 0;
  currentMonth = new Date();
  selectedDay = this.startOfDay(new Date());

  constructor(
    private meetingsService: MeetingsService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadStaffOptions();
    this.loadMeetings();
  }

  loadMeetings(): void {
    this.loading = true;
    this.meetingsService.getMine(this.currentMonth).subscribe({
      next: (rows) => {
        this.meetings = rows || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load meetings.', 'error');
      }
    });
  }

  loadStaffOptions(): void {
    this.meetingsService.getStaffOptions().subscribe({
      next: (rows) => { this.staffOptions = rows || []; },
      error: () => { this.staffOptions = []; }
    });
  }

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(MeetingCreateDialogComponent, {
      width: '860px',
      maxWidth: '96vw',
      panelClass: ['app-modal-panel', 'meeting-create-panel'],
      backdropClass: 'app-modal-backdrop',
      data: { staffOptions: this.staffOptions }
    });

    dialogRef.afterClosed().subscribe((payload) => {
      if (!payload) return;

      this.meetingsService.create(payload).subscribe({
        next: () => this.loadMeetings(),
        error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to create meeting.', 'error')
      });
    });
  }

  openMeeting(meeting: any): void {
    this.meetingsService.getById(meeting.id).subscribe({
      next: (row) => {
        const dialogRef = this.dialog.open(MeetingDetailDialogComponent, {
          width: '900px',
          maxWidth: '96vw',
          panelClass: ['app-modal-panel', 'meeting-detail-panel'],
          backdropClass: 'app-modal-backdrop',
          data: { meeting: row, staffOptions: this.staffOptions }
        });

        dialogRef.afterClosed().subscribe(() => this.loadMeetings());
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to load meeting details.', 'error')
    });
  }

  joinMeeting(meeting: any, event?: Event): void {
    event?.stopPropagation();
    if (!meeting?.id) return;
    this.dialog.closeAll();
    this.router.navigateByUrl(`/meetings/${meeting.id}/room`);
  }

  previousMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    this.selectedDay = this.startOfDay(new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1));
    this.loadMeetings();
  }

  nextMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.selectedDay = this.startOfDay(new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1));
    this.loadMeetings();
  }

  selectDay(day: Date): void {
    this.selectedDay = this.startOfDay(day);
  }

  get todayMeetings(): any[] {
    const today = this.startOfDay(new Date());
    return this.meetings.filter((meeting) => this.isSameDay(new Date(meeting.startTime), today));
  }

  get ongoingMeetings(): any[] {
    const now = new Date();
    return this.meetings
      .filter((meeting) =>
        String(meeting.status) !== 'Cancelled'
        && String(meeting.status) !== 'Completed'
        && new Date(meeting.startTime) <= now
        && new Date(meeting.endTime) >= now)
      .slice(0, 8);
  }

  get upcomingMeetings(): any[] {
    const now = new Date();
    return this.meetings
      .filter((meeting) =>
        String(meeting.status) !== 'Cancelled'
        && String(meeting.status) !== 'Completed'
        && new Date(meeting.startTime) > now)
      .slice(0, 8);
  }

  get completedMeetings(): any[] {
    return this.meetings
      .filter((meeting) => String(meeting.status) === 'Completed' || new Date(meeting.endTime) < new Date())
      .slice(0, 8);
  }

  get selectedDayMeetings(): any[] {
    return this.meetings
      .filter((meeting) => this.isSameDay(new Date(meeting.startTime), this.selectedDay))
      .sort((left, right) => new Date(left.startTime).getTime() - new Date(right.startTime).getTime());
  }

  get calendarWeeks(): Array<Array<{ date: Date; inMonth: boolean; meetings: any[] }>> {
    const firstDayOfMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
    const gridStart = new Date(firstDayOfMonth);
    gridStart.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());

    const weeks: Array<Array<{ date: Date; inMonth: boolean; meetings: any[] }>> = [];
    for (let week = 0; week < 6; week += 1) {
      const row: Array<{ date: Date; inMonth: boolean; meetings: any[] }> = [];
      for (let day = 0; day < 7; day += 1) {
        const date = new Date(gridStart);
        date.setDate(gridStart.getDate() + (week * 7) + day);
        row.push({
          date,
          inMonth: date.getMonth() === this.currentMonth.getMonth(),
          meetings: this.meetings.filter((meeting) => this.isSameDay(new Date(meeting.startTime), date))
        });
      }
      weeks.push(row);
    }
    return weeks;
  }

  dayLabel(date: Date): string {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  }

  monthLabel(): string {
    return this.currentMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }

  isSelectedDay(date: Date): boolean {
    return this.isSameDay(date, this.selectedDay);
  }

  isLiveMeeting(meeting: any): boolean {
    if (!meeting) return false;
    const status = String(meeting.status || '');
    if (status === 'Completed' || status === 'Cancelled') return false;
    const now = new Date().getTime();
    const start = new Date(meeting.startTime).getTime();
    const end = new Date(meeting.endTime).getTime();
    return start <= now && end >= now;
  }

  toneClass(meeting: any): string {
    const room = String(meeting?.roomType || '').toLowerCase();
    if (String(meeting?.status) === 'Cancelled') return 'cancelled';
    if (room === 'audio') return 'audio';
    return 'video';
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private isSameDay(left: Date, right: Date): boolean {
    return left.getFullYear() === right.getFullYear()
      && left.getMonth() === right.getMonth()
      && left.getDate() === right.getDate();
  }
}
